// Bottom-sheet Settings. Today: just the Account section (sign-in / sign-out).
// Designed to grow — additional sections (Theme, Language, About) can slot in
// below without re-shaping this component.

import { LogIn, LogOut, X } from 'lucide-react';
import type { AuthUser } from '@/lib/auth/auth';
import { C } from '@/styles/tokens';

interface SettingsSheetProps {
  user: AuthUser | null;
  onSignIn: () => void;
  onSignOut: () => void;
  onClose: () => void;
}

export function SettingsSheet({
  user,
  onSignIn,
  onSignOut,
  onClose,
}: SettingsSheetProps) {
  return (
    <div role="dialog" aria-modal aria-label="Settings" className="fixed inset-0 z-50 flex flex-col">
      <div
        className="absolute inset-0 anim-backdrop-in"
        style={{ background: 'rgba(26,22,20,0.45)' }}
        aria-hidden
        onClick={onClose}
      />
      <div
        className="relative mt-auto flex flex-col rounded-t-[28px] overflow-hidden anim-sheet-up"
        style={{ background: C.cream, boxShadow: '0 -10px 30px rgba(26,22,20,0.22)', maxHeight: '85vh' }}
      >
        <span
          aria-hidden
          className="absolute top-2 left-1/2 w-10 h-1 rounded-full"
          style={{ transform: 'translateX(-50%)', background: 'rgba(26,22,20,0.18)' }}
        />

        <header
          className="pt-6 pb-3 px-5 flex items-center justify-between"
          style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
        >
          <div className="ff-display italic text-2xl flex items-baseline gap-2" style={{ color: C.ink }}>
            Settings
            <span className="ff-zh-serif not-italic text-base opacity-50">· 设置</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close settings"
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: 'white' }}
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <Section title="Account" hz="账户">
            {user ? (
              <>
                <Row label="Signed in as" value={user.email ?? user.displayName ?? user.id} />
                <button
                  type="button"
                  onClick={onSignOut}
                  className="w-full mt-3 py-3 rounded-full ff-body text-[12px] font-bold uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2"
                  style={{ background: C.vermillion, color: 'white' }}
                >
                  <LogOut size={14} aria-hidden /> Sign out
                  <span className="ff-zh-serif tracking-normal opacity-80">退出</span>
                </button>
              </>
            ) : (
              <>
                <p className="ff-body text-[13px] leading-relaxed mb-3" style={{ color: C.inkLight }}>
                  You&apos;re using HanZi World without an account. Your data lives only on this device. Sign in
                  to sync across devices.
                </p>
                <button
                  type="button"
                  onClick={onSignIn}
                  className="w-full py-3 rounded-full ff-body text-[12px] font-bold uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2"
                  style={{ background: C.ink, color: 'white' }}
                >
                  <LogIn size={14} aria-hidden /> Sign in
                  <span className="ff-zh-serif tracking-normal opacity-80">登录</span>
                </button>
              </>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  hz,
  children,
}: {
  title: string;
  hz?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-4">
      <div className="flex items-baseline gap-2 mb-2" style={{ color: C.inkLight }}>
        <span className="ff-body text-[10px] uppercase tracking-[0.3em]">{title}</span>
        {hz && <span className="ff-zh-serif text-[12px] opacity-80">· {hz}</span>}
      </div>
      <div
        className="p-4 rounded-2xl"
        style={{ background: 'white', boxShadow: '0 1px 4px rgba(26,22,20,0.04)' }}
      >
        {children}
      </div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="ff-body text-[10px] uppercase tracking-[0.2em] mb-0.5" style={{ color: C.inkSofter }}>
        {label}
      </div>
      <div className="ff-body text-[13px] font-semibold truncate" style={{ color: C.ink }}>
        {value}
      </div>
    </div>
  );
}

